import React, { useEffect, useMemo, useRef } from "react";
import RenderProducts from "./RenderProducts";
import { makeStyles } from "@material-ui/core/styles";
import CategoryStyle from "../../../views/CategoryPage/CategoryStyle";
import { flat, fontName, is_open } from "../../../constants/utils";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { NavHashLink } from "react-router-hash-link";
import useMeasure from "react-use/lib/useMeasure";
import { useHistory } from "react-router-dom";
import { InView } from "react-intersection-observer";
import { css } from "@emotion/css";
import debounce from "lodash/debounce";

const useStyles = makeStyles(CategoryStyle);

const IGNORE_AFTER_REFRESH_MS = 800; // 0.8 seconds
const CLICK_GUARD_MS = 1500; // 1.5 seconds

export default function RenderSpecialCategory(props) {
  const history = useHistory();
  const classes = useStyles();
  const menuInfo = useSelector((state) => state.menuInfo);
  const { menuData } = menuInfo;
  const [ref, bounds] = useMeasure();
  const { timezone, categories, theme_data } = menuData;
  const { currentCategory, handleCartButton } = props;
  const {
    description,
    view_format,
    special_category_type,
    special_category_display_categories,
  } = currentCategory;

  const all_products = useMemo(() => {
    return flat(
      categories
        .filter(
          (category) =>
            !category.disable_link &&
            !category.external_link &&
            !category.enable_special_category &&
            is_open(category.category_hours, [], timezone)
        )
        .map((category) => category.products)
    )
      .filter((product) =>
        special_category_type === "discounted"
          ? product.discount > 0
          : special_category_type === "combos"
          ? product.enable_combo
          : product
      )
      .filter((product) => is_open(product.product_hours, [], timezone));
  }, [categories, special_category_type, timezone]);

  const all_categories = useMemo(() => {
    return categories
      .filter((category) => is_open(category.category_hours, [], timezone))
      .filter((category) =>
        special_category_type === "all"
          ? category.products.length > 0
          : special_category_type === "all_in_category"
          ? category.parent === currentCategory.id &&
            category.products.length > 0
          : category.products.filter((product) => product.discount > 0).length >
            0
      );
  }, [categories, currentCategory, special_category_type, timezone]);

  const navClasses = classNames({
    [classes.scrollCategoryNav]: true,
    [classes.stickyNav]: true,
    "sticky-nav": true,
  });

  const navItemClasses = classNames({
    [classes.navItem]: true,
    [css`
      background-color: ${theme_data.category_screen_nav_button_color};
      &.is-active {
        background-color: ${theme_data?.category_screen_nav_button_selected_color};
      }
    `]: true,
  });

  const navItemStyle = {
    fontSize: Number(theme_data?.category_screen_nav_button_font_size),
    color: theme_data?.category_screen_nav_button_text_color,
    ...fontName(theme_data?.category_screen_nav_button_font_family),
  };

  const catTitleStyle = {
    fontSize: Number(
      theme_data?.category_screen_special_category_title_font_size
    ),
    color: theme_data?.category_screen_special_category_title_text_color,
    textTransform:
      theme_data?.category_screen_special_category_title_text_transform,
    ...fontName(theme_data?.category_screen_special_category_title_font_family),
  };

  const firstLoadRef = useRef(true);
  const skipInViewRef = useRef(false);

  useEffect(() => {
    if (firstLoadRef.current) {
      // the initial menuData arrives → allow the first InView
      firstLoadRef.current = false;
      return;
    }
    // on every *subsequent* menuData update, skip InViews briefly
    skipInViewRef.current = true;
    const t = setTimeout(() => {
      skipInViewRef.current = false;
    }, IGNORE_AFTER_REFRESH_MS);
    return () => clearTimeout(t);
  }, [categories]);

  // ref to hold the nav element
  const navRef = useRef();

  // ref to hold the ID of the category the user just clicked
  const clickedCategoryRef = useRef(null);

  const clickTimeout = useRef(null);

  // call this on nav-link click
  const handleCategoryClick = (id) => {
    clickedCategoryRef.current = id;
    clearTimeout(clickTimeout.current);
    clickTimeout.current = setTimeout(() => {
      clickedCategoryRef.current = null;
    }, CLICK_GUARD_MS);
  };

  // If this component unmounts during that 0.8s you’ll leave a dangling timer
  useEffect(() => () => clearTimeout(clickTimeout.current), []);

  // A debounced helper that BOTH replaces the hash and recentres the nav
  // your existing replace+centre logic, debounced
  const replaceAndCenter = useMemo(
    () =>
      debounce((hash) => {
        history.replace(hash);
        const active = navRef.current?.querySelector(".is-active");
        active?.scrollIntoView({ inline: "center", behavior: "smooth" });
      }, 200),
    [history]
  );

  const catInView = (category, inView) => {
    // if data just refreshed, skip stray inView
    if (skipInViewRef.current) return;

    if (!inView) return;

    // if the user just clicked a different category, ignore
    if (
      clickedCategoryRef.current !== null &&
      clickedCategoryRef.current !== category.id
    ) {
      return;
    }

    replaceAndCenter(`#cat-${category.id}`);
  };

  // clean up debounce on unmount
  useEffect(() => {
    return () => replaceAndCenter.cancel();
  }, [replaceAndCenter]);

  return (
    <div
      style={{
        marginTop: description || special_category_type === "combos" ? 0 : 90,
      }}
      ref={ref}
    >
      {description && (
        <div
          className={classes.categoryDescription}
          style={{
            marginTop:
              menuInfo.isOpen && special_category_display_categories ? 70 : 20,
            marginBottom: 20,
          }}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}
      {!special_category_display_categories && (
        <RenderProducts
          products={all_products}
          handleCartButton={handleCartButton}
          view_format={view_format}
          category={currentCategory}
        />
      )}
      {special_category_display_categories && (
        <>
          <div
            className={navClasses}
            style={{
              maxWidth: bounds.width > 450 ? "100%" : bounds.width + 30,
            }}
            ref={navRef}
          >
            {all_categories.map((category) => (
              <NavHashLink
                key={category.id}
                smooth
                to={`#cat-${category.id}`}
                onClick={() => handleCategoryClick(category.id)}
                activeClassName="is-active"
                className={navItemClasses}
                style={navItemStyle}
                replace
              >
                {category.name}
              </NavHashLink>
            ))}
          </div>

          {all_categories.map((category) => (
            <div key={category.id}>
              <InView
                id={`cat-${category.id}`}
                threshold={1}
                onChange={(inView) => catInView(category, inView)}
                as="div"
                style={{
                  scrollMarginTop: 150,
                }}
              >
                {category.image?.product ? (
                  <img
                    src={category.image?.product}
                    className={classes.categoryImage}
                    width={category?.image_width}
                    height={category?.image_height}
                    alt={category.name}
                    style={{ maxWidth: "100%", width: "100%" }}
                  />
                ) : (
                  <div
                    className={classes.specialCategoryName}
                    style={catTitleStyle}
                  >
                    {category.name}
                  </div>
                )}
              </InView>

              <RenderProducts
                products={
                  special_category_type === "discounted"
                    ? category.products.filter(
                        (product) => product.discount > 0
                      )
                    : category.products
                }
                handleCartButton={handleCartButton}
                view_format={view_format}
                category={category}
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
}
